import React, { useState, useEffect, lazy } from 'react'
import { Row, Card, Separator, Paragraph, useContainerWidth } from '@fs/zion-ui'
import { Link, useHistory } from '@fs/zion-router'
import { ContentLock, ContentUnlock, ContentEdit, ContentCheck, ContentAdd } from '@fs/zion-icon'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import Timestamp from '../Timestamp'
import { InPersonBadge, SessionDifficultyBadge, OnlineBadge } from '../Badge'
import { getRelativePath, getDuration, parseMsToISO, getDuplicatesAcrossArrays } from '../helpers'
import VideoThumbnail from './CardListItem/VideoThumbnail'
import { MonoText } from '../type'
import { StrongLink } from '../link'
import { useHeadingLevel, useLazyRequest } from '../../hooks'
import { getSessionRatings } from '../../api'
import { conferenceConfig, sessionStates, sessionWorkflowStates } from '../../config'
import { useTimeTravel } from '../../providers'
import AgreementButton from '../../components/dashboard/components/AgreementButton'
import { PillButton } from '../buttons'
import { HeaderBadges } from './NewCard'
import { proposalPlaceholderPng } from '../../images'

const ArchiveActions = lazy(() => import('../buttons/ArchiveActionsButtons'))

const thumbnailCss = css`
  width: 100px;
  // inherit border radius from card
  border-radius: var(--roundiness);
  // force the border radius when the child has sharp corners
  overflow: hidden;
`

const Badges = ({ promo, conferenceYear, date }) => {
  const location = promo?.sessionLocation
  return (
    <Row>
      <div growX>
        <Row gutters="nano" wrap>
          {location?.includes('In Person') && <InPersonBadge />}
          {location?.includes('Online') && <OnlineBadge />}
          {promo?.sessionDifficulty && <SessionDifficultyBadge sessionDifficulty={promo?.sessionDifficulty} />}
        </Row>
      </div>
      <Row guttersX="none">
        <Timestamp conferenceYear={conferenceYear} timestamp={date} />
      </Row>
    </Row>
  )
}

const ThumbnailContainer = ({ data, to, hideThumbnailBadges }) => {
  const { title } = data
  const thumbnailLinkName = `Watch: ${title}: Thumbnail`
  const videoDuration = getDuration(data)
  // should be the default or the uploaded image; getCustomImage prioritizes the speaker profile image before the placeholder
  const thumbnail = data?.video?.thumbnail?.attributes?.src || proposalPlaceholderPng
  const Container = to ? Link : 'div'
  const props = to ? { to, linkName: thumbnailLinkName } : {}

  return (
    <Container {...props}>
      <div css={thumbnailCss}>
        <VideoThumbnail
          thumbnail={thumbnail}
          duration={videoDuration}
          heading={title}
          hideThumbnailBadges={hideThumbnailBadges}
        />
      </div>
    </Container>
  )
}

const Actions = ({ data, isEditable, path, userHasSigned, showContract, isAdmin }) => {
  const [t] = useTranslation()
  const history = useHistory()

  const { getNow } = useTimeTravel()
  const disableAttachments =
    !isAdmin &&
    parseMsToISO(getNow()) > conferenceConfig[Number.parseInt(conferenceConfig.year, 10)].attachmentsEditDeadline

  if (showContract) {
    const users = data?.creators?.map(({ associatedUser }) => associatedUser)
    const hasSameCreatorInMultipleLanguages = getDuplicatesAcrossArrays(...users).length > 0
    return (
      <>
        <Separator size="xs" />
        <Row gutters="md">
          <AgreementButton
            userHasSigned={userHasSigned}
            sessionId={data.id}
            locale={data.contentLocale}
            disabled={hasSameCreatorInMultipleLanguages}
          />
        </Row>
        {hasSameCreatorInMultipleLanguages && (
          <>
            <Separator size="nano" />
            <Paragraph secondary size="sm">
              <Trans
                i18nKey="dashboard.session-card.contract-issue"
                defaults="Contract cannot be signed. Please contact <MailLink>speakers@rootstech.org</MailLink> for help."
                components={{
                  MailLink: (
                    <Link to="mailto:speakers@rootstech.org" linkName="Contact Us: Speakers" target="_blank" external />
                  ),
                }}
              />
            </Paragraph>
          </>
        )}
      </>
    )
  }
  const syllabusId = data?.classSyllabus?.id || ''
  const slidesId = data?.slideDeck?.id || ''
  const onAddContentClick = (attachmentId, fileType, sessionId) => {
    history.push(`/dashboard/attachment/${attachmentId}?fileType=${fileType}&sessionId=${sessionId}`)
  }
  return (
    <>
      <Separator size="xs" />
      <Row gutters="md">
        <PillButton
          emphasis="medium"
          data-testid="edit-session-button"
          disabled={!isEditable}
          Icon={ContentEdit}
          to={`${path}${data.id}?lang=${data.contentLocale}`}
        >
          {t('common-ui:edit.action', 'Edit')}
        </PillButton>
        <PillButton
          onClick={() => onAddContentClick(syllabusId, 'syllabus', data.id)}
          emphasis="low"
          disabled={disableAttachments}
          Icon={syllabusId ? ContentCheck : ContentAdd}
          color="blue"
        >
          {t('upload-content.syllabus.label', 'Syllabus')}
        </PillButton>
        <PillButton
          onClick={() => onAddContentClick(slidesId, 'slides', data.id)}
          emphasis="low"
          disabled={disableAttachments}
          Icon={slidesId ? ContentCheck : ContentAdd}
          color="blue"
        >
          {t('upload-content.slides.label', 'Slides')}
        </PillButton>
      </Row>
    </>
  )
}

const ResponsiveContainer = ({ thumbnail, badges, children, dataTestId }) => {
  const atWidth = useContainerWidth()
  const dense = atWidth({ default: true, md: false })

  return dense ? (
    <Card data-testid={dataTestId}>
      <Row>
        {thumbnail}
        {badges}
      </Row>
      {children}
    </Card>
  ) : (
    <Card data-testid={dataTestId}>
      <Row>
        {thumbnail}
        <div growX>
          {badges}
          {children}
        </div>
      </Row>
    </Card>
  )
}

export default function DashboardSessionCard({
  data,
  showArchiveButton,
  removeSession,
  path = '/dashboard/sessions',
  profiles = [],
  headingLevel = 'h3',
  hideThumbnailBadges = false,
  isAdmin,
}) {
  const [t] = useTranslation()
  const { title, url, id: sessionId, promo, conferenceYear, date } = data
  const { Heading } = useHeadingLevel(headingLevel)
  const isNewSession = conferenceYear === conferenceConfig.year

  const workflowState =
    data.visibility.status !== sessionStates.WORKFLOW
      ? sessionStates[data.visibility.status]
      : sessionWorkflowStates[data.visibility.workflowState]
  const isEditable = !!(workflowState === sessionStates.DRAFT || workflowState === sessionStates.EDITABLE)
  const showContract =
    data?.agreements?.length < data?.creators?.length &&
    path.includes('/dashboard') &&
    workflowState === sessionStates.ACCEPTED
  const userHasSigned = data?.agreements?.some((agreement) => profiles.some((p) => p.id === agreement.creator?.id))

  const [getRatings, { loading, data: ratings }] = useLazyRequest(getSessionRatings)
  const [likes, setLikes] = useState(0)
  useEffect(() => {
    getRatings({ sessionId })
  }, [getRatings, sessionId])
  useEffect(() => {
    if (ratings) {
      setLikes(ratings.score)
    }
  }, [loading, ratings])

  const to = workflowState === sessionStates.PUBLISHED ? getRelativePath(url) : null
  const linkName = `Watch: ${title}: Title`

  return (
    <ResponsiveContainer
      thumbnail={<ThumbnailContainer data={data} to={to} hideThumbnailBadges={hideThumbnailBadges} />}
      dataTestId={`session-card-${sessionId}`}
      badges={
        isNewSession ? (
          <HeaderBadges workflowState={workflowState} />
        ) : (
          <Badges promo={promo} conferenceYear={conferenceYear} date={date} />
        )
      }
    >
      <Separator size="xxs" />
      <Row gutters="xs">
        <Heading size="H5">
          <MonoText>
            <StrongLink to={to} noUnderline linkName={linkName}>
              {title}
            </StrongLink>
          </MonoText>
        </Heading>
        {!isEditable && <ContentLock data-testid="session-lock-icon" color="gray" size="sm" />}
        {isNewSession && isEditable && <ContentUnlock data-testid="session-unlock-icon" color="gray" size="sm" />}
      </Row>
      <Separator size="xxs" />
      {!isNewSession && (
        <Paragraph secondary size="sm">
          {t('dashboard.past-sessions.likes', 'Likes: {likes}', { likes })}
        </Paragraph>
      )}
      {isNewSession && (
        <Actions
          data={data}
          isEditable={isEditable}
          path={path}
          showContract={showContract}
          userHasSigned={userHasSigned}
          isAdmin={isAdmin}
        />
      )}
      {!isNewSession && showArchiveButton && <ArchiveActions data={data} removeData={removeSession} />}
    </ResponsiveContainer>
  )
}
